import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Waves, Globe, Heart, Sparkles, TrendingUp, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import AISupportChatbot from '@/components/AISupportChatbot';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-ocean-light/10">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-ocean-light/20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/servpac-logo.png" 
                alt="ServPac Logo" 
                className="w-10 h-10 object-contain"
              />
              <h1 data-testid="app-logo" className="text-2xl font-chivo font-bold text-ocean-deep">
                ServPac
              </h1>
            </div>
            <div className="flex gap-4">
              <Button
                data-testid="header-login-btn"
                variant="ghost"
                className="text-ocean-deep hover:text-ocean-turquoise"
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
              <Button
                data-testid="header-register-btn"
                className="bg-coral hover:bg-coral-dark"
                onClick={() => navigate('/register')}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative py-20 md:py-32 overflow-hidden"
        style={{
          backgroundImage: 'linear-gradient(135deg, rgba(12, 74, 110, 0.95) 0%, rgba(20, 184, 166, 0.85) 100%), url(https://images.unsplash.com/photo-1559827260-dc66d52bef19?q=80&w=2070)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'multiply'
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-48 h-48 border-4 border-white/50 rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 border-4 border-white/30 rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6 border border-white/30">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">From the Pacific Islands to the World</span>
            </div>
            
            <h2 data-testid="hero-heading" className="text-5xl md:text-7xl font-chivo tracking-tight font-black mb-6 leading-tight">
              Serving Pacific
              <br />
              <span className="text-coral">Trade Worldwide</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed max-w-3xl mx-auto">
              Pacific trade made easy. Connect Pacific Island exporters with global importers. 
              Tropical fruits, seafood, handicrafts - farmers Pacific marketplace.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="hero-seller-btn"
                size="lg"
                className="bg-coral hover:bg-coral-dark text-white px-8 py-6 text-lg font-semibold shadow-lg"
                onClick={() => navigate('/register')}
              >
                <Heart className="w-5 h-5 mr-2" />
                Start Selling
              </Button>
              <Button
                data-testid="hero-buyer-btn"
                size="lg"
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/50 px-8 py-6 text-lg backdrop-blur-sm"
                onClick={() => navigate('/marketplace')}
              >
                <Globe className="w-5 h-5 mr-2" />
                Explore Products
              </Button>
            </div>

            <p className="mt-6 text-sm text-white/70">
              Join 1,000+ Pacific Islander sellers already exporting worldwide
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h3 className="text-3xl md:text-5xl font-chivo font-bold text-ocean-deep mb-6">
              Pacific Export & Import Made Easy
            </h3>
            <p className="text-lg text-slate-600 leading-relaxed">
              ServPac - Serve Pacific trade. We connect Pacific Island farmers, exporters, and artisans 
              with global markets. From Fiji to Samoa, Tonga to Vanuatu - your gateway to Pacific commodities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card data-testid="value-authentic" className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-ocean-turquoise">
              <div className="w-16 h-16 bg-ocean-turquoise/10 rounded-full flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-ocean-turquoise" />
              </div>
              <h4 className="text-2xl font-chivo font-semibold mb-3 text-ocean-deep">100% Authentic</h4>
              <p className="text-slate-600 leading-relaxed">
                Every product tells a story. From handwoven baskets to fresh tropical produce, 
                discover genuine Pacific Islander craftsmanship and traditions.
              </p>
            </Card>

            <Card data-testid="value-fair" className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-coral">
              <div className="w-16 h-16 bg-coral/10 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-coral" />
              </div>
              <h4 className="text-2xl font-chivo font-semibold mb-3 text-ocean-deep">Fair Trade</h4>
              <p className="text-slate-600 leading-relaxed">
                Direct connection between sellers and buyers. No middlemen. Fair prices. 
                Your purchase directly supports island families and communities.
              </p>
            </Card>

            <Card data-testid="value-global" className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-ocean">
              <div className="w-16 h-16 bg-ocean/10 rounded-full flex items-center justify-center mb-6">
                <Globe className="w-8 h-8 text-ocean" />
              </div>
              <h4 className="text-2xl font-chivo font-semibold mb-3 text-ocean-deep">Global Reach</h4>
              <p className="text-slate-600 leading-relaxed">
                Integrated freight quotes and logistics. Ship your island products anywhere in the world 
                with transparent pricing and reliable delivery.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-ocean-deep to-ocean text-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-5xl font-chivo font-bold mb-4">
              What You Can Sell
            </h3>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              From farm to craft, showcase your Pacific Island products to the world
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '🌴', name: 'Agricultural Produce', desc: 'Tropical fruits, vegetables, taro, fresh crops' },
              { icon: '☕', name: 'Agricultural Commodities', desc: 'Coffee, cocoa, copra, vanilla, spices' },
              { icon: '🎨', name: 'Handcrafted Items', desc: 'Woven baskets, tapa cloth, carvings' },
              { icon: '💍', name: 'Jewelry & Accessories', desc: 'Shell necklaces, bone carvings, pearls' },
              { icon: '💎', name: 'Minerals & Gemstones', desc: 'Black pearls, coral, volcanic stones' },
              { icon: '🌺', name: 'Other Products', desc: 'Unique Pacific Island items' }
            ].map((category, idx) => (
              <Card key={idx} className="bg-white/10 backdrop-blur-sm border-white/20 p-6 hover:bg-white/20 transition-all">
                <div className="text-5xl mb-4">{category.icon}</div>
                <h4 className="text-xl font-chivo font-semibold mb-2">{category.name}</h4>
                <p className="text-sm text-white/70">{category.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-sand/30">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h3 className="text-3xl md:text-5xl font-chivo font-bold text-center mb-12 text-ocean-deep">
            How ServPac Works
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-ocean-turquoise rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h4 className="text-xl font-chivo font-semibold mb-3 text-ocean-deep">List Your Products</h4>
              <p className="text-slate-600">
                Register as a seller, upload photos, set your prices, and share your island story
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-coral rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h4 className="text-xl font-chivo font-semibold mb-3 text-ocean-deep">Connect with Buyers</h4>
              <p className="text-slate-600">
                Global customers discover your products. Chat directly, answer questions, build relationships
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-ocean rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h4 className="text-xl font-chivo font-semibold mb-3 text-ocean-deep">Ship & Get Paid</h4>
              <p className="text-slate-600">
                Get instant freight quotes, arrange shipping, and receive payments securely
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-ocean-deep text-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-chivo font-black text-coral mb-2">1,000+</div>
              <p className="text-white/70">Island Sellers</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-chivo font-black text-ocean-turquoise mb-2">50+</div>
              <p className="text-white/70">Countries Reached</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-chivo font-black text-coral mb-2">$2M+</div>
              <p className="text-white/70">Exports Facilitated</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-chivo font-black text-ocean-turquoise mb-2">100%</div>
              <p className="text-white/70">Pacific Owned</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-coral-dark to-coral text-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <Waves className="w-16 h-16 mx-auto mb-6 animate-wave" />
          <h3 className="text-3xl md:text-5xl font-chivo font-bold mb-6">
            Ready to Share Your Island Heritage?
          </h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Join Pacific Islander artisans, farmers, and entrepreneurs who are building thriving 
            businesses while preserving their cultural traditions.
          </p>
          <Button
            data-testid="footer-cta-btn"
            size="lg"
            className="bg-white text-coral hover:bg-sand px-10 py-6 text-lg font-bold shadow-xl"
            onClick={() => navigate('/register')}
          >
            <TrendingUp className="w-5 h-5 mr-2" />
            Start Your Export Journey
          </Button>
          <p className="mt-6 text-sm text-white/70">
            Free to join • No listing fees • Get paid directly
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-ocean-deep text-white py-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/images/logo.png" 
                  alt="ServPac Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="font-chivo font-bold text-xl">ServPac</span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Empowering Pacific Islander communities through global trade. 
                Connecting island heritage with worldwide markets.
              </p>
            </div>

            <div>
              <h4 className="font-chivo font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="/marketplace" className="text-white/70 hover:text-ocean-turquoise transition-colors">
                    Browse Marketplace
                  </a>
                </li>
                <li>
                  <a href="/register" className="text-white/70 hover:text-ocean-turquoise transition-colors">
                    Become a Seller
                  </a>
                </li>
                <li>
                  <a href="/login" className="text-white/70 hover:text-ocean-turquoise transition-colors">
                    Sign In
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-chivo font-semibold text-lg mb-4">Connect With Us</h4>
              <div className="flex gap-4">
                <a
                  href="https://www.facebook.com/people/Pacific-Export-Hub/61587931000286/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="facebook-link"
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/pace.xporthub/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="instagram-link"
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center text-sm text-white/60">
            <p>© 2026 ServPac. Empowering Pacific Islander communities through global trade.</p>
          </div>
        </div>
      </footer>

      {/* AI Support Chatbot - Bottom Left */}
      <AISupportChatbot />
    </div>
  );
};

export default Landing;
